import { NavLink } from "react-router-dom";

/**
 * A COLLAPSIBLE SIDEBAR SECTION — Part 1 (navigation foundation).
 *
 * The one shared mechanism. There is no per-department variant and there must never be: a profile
 * asks for this behaviour by declaring `collapsible: true` on its section, and the difference between
 * departments stays in `utils/navigationProfiles.js` where every other navigation difference lives.
 *
 * ── THE TRIGGER IS A BUTTON, NOT A LINK ─────────────────────────────────────
 * It goes nowhere. Rendering it as an anchor would promise a destination that does not exist, break
 * middle-click and Cmd-click, and announce as "link" to a screen reader when the thing it does is
 * expand. `<button>` also gives Enter AND Space activation for free, which is exactly the contract —
 * a hand-rolled `div` + `onKeyDown` would have had to re-implement both and would still not be
 * announced correctly.
 *
 * ── IT OWNS NO STATE ────────────────────────────────────────────────────────
 * `open` and `onToggle` are passed in. The accordion — one section open at a time, and the route
 * forcing its own section open — is decided by `resolveOpenSection` in `navigationProfiles.js`, so
 * that decision is a pure function this component cannot contradict. Two components each holding
 * their own boolean is how "only one open" quietly stops being true.
 *
 * ── ACTIVE ROW DETECTION IS NOT DUPLICATED ──────────────────────────────────
 * `isActiveRow` is injected and is the caller's single `isRowActive` opinion. This file does not
 * compare a pathname to anything; if it did, the highlight and `aria-current` could disagree with
 * the flat rows rendered beside them.
 */
export default function CollapsibleNavSection({
  id,
  label,
  icon,
  items,
  open,
  onToggle,
  isActiveRow,
  hasActiveRow,
  onNavigate,
}) {
  // `visibleSections` has already dropped the rows this actor may not use, and drops a section whose
  // every row was dropped. A defensive check here as well, because a collapsed group with nothing
  // inside it is a control that opens onto nothing.
  if (!items || items.length === 0) return null;

  const panelId = `nav-sec-${id}`;
  const buttonId = `nav-sec-btn-${id}`;

  return (
    <div className="nav-group">
      <button
        type="button"
        id={buttonId}
        className={`nav-section nav-section-toggle${hasActiveRow ? " has-active" : ""}`}
        // The FACT of the state, for anyone who cannot see the chevron. `<button>` already announces
        // its role, so this is the only thing a screen reader is otherwise missing.
        aria-expanded={open ? "true" : "false"}
        aria-controls={panelId}
        onClick={onToggle}
      >
        {icon && <i className={`bi ${icon} nav-section-icon`} aria-hidden="true" />}
        <span className="nav-section-label">{label}</span>
        {/* Decorative: the chevron restates `aria-expanded`, so announcing it too would be noise. */}
        <i
          className={`bi ${open ? "bi-chevron-down" : "bi-chevron-right"} nav-section-chevron`}
          aria-hidden="true"
        />
      </button>

      {/* Unmounted rather than hidden when closed, so a collapsed group's rows are not reachable by
          Tab. A `display:none` panel would keep them in the accessibility tree of some engines and
          let focus land on a row the reader cannot see. */}
      {open && (
        <div id={panelId} role="group" aria-labelledby={buttonId} className="nav-group-items">
          {items.map((row) => (
            <NavLink
              key={row.to}
              to={row.to}
              end={row.end}
              /* The callback form is load-bearing for the same reason it is in `Sidebar.jsx`: given a
                 string, `NavLink` appends its own pathname-only "active" class on top, and a computed
                 class could not withhold the highlight from a sibling sharing the route. */
              className={() => `nav-link nav-link-child${isActiveRow(row) ? " active" : ""}`}
              /* `: false`, NEVER `: undefined` — the same load-bearing detail as the className
                 callback beside it, and for the same reason. `NavLink` declares its own prop as
                 `"aria-current": ariaCurrentProp = "page"`, a DEFAULT PARAMETER that fires on
                 `undefined`, then applies it wherever its pathname-only match succeeds. Passing
                 `undefined` therefore ANNOUNCED every row sharing a pathname as the current page
                 while highlighting only one — found in the browser on the Part 3 queue rows, where
                 four children share `/tasks/queue`. `false` suppresses the default and renders
                 `aria-current="false"`, which is valid ARIA for "not the current item". */
              aria-current={isActiveRow(row) ? "page" : false}
              onClick={onNavigate}
            >
              <i className={`bi ${row.icon}`} aria-hidden="true" />
              <span>{row.label}</span>
              {/* BADGE CAPABILITY, NOT A BADGE. Rendered only when a row actually carries a value —
                  no count is fabricated and no endpoint is assumed. No row declares one today, so
                  nothing renders; when a real count source exists this is where it lands. */}
              {row.badge != null && row.badge !== "" && (
                <span
                  className={`nav-badge${row.badgeType ? ` nav-badge-${row.badgeType}` : ""}`}
                  aria-label={row.badgeAriaLabel || undefined}
                >
                  {row.badge}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}
